import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import {
  Controller,
  ControllerProps,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  FormProvider,
  useFormContext
} from "react-hook-form";

import styles from "./form.module.scss";

const Form = FormProvider;

interface FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  name: TName;
}

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  return {
    name: fieldContext.name,
    ...fieldState
  };
};

interface FormItemContextValue {
  id: string;
}

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue
);

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div
        ref={ref}
        className={`${styles.item}${className ? ` ${className}` : ""}`}
        {...props}
      />
    </FormItemContext.Provider>
  );
});

const FormLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => {
  const { error } = useFormField();

  return (
    <label
      ref={ref}
      className={`${error ? styles.labelError : ""}${className ? ` ${className}` : ""}`.trim()}
      {...props}
    />
  );
});

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot>
>(({ ...props }, ref) => {
  return <Slot ref={ref} {...props} />;
});

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={`${styles.description}${className ? ` ${className}` : ""}`}
      {...props}
    />
  );
});

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error } = useFormField();
  const body = error ? String(error?.message) : children;

  if (!body || body === "undefined") {
    return null;
  }

  return (
    <p
      ref={ref}
      className={`${styles.message}${className ? ` ${className}` : ""}`}
      {...props}
    >
      {body}
    </p>
  );
});

export interface FieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TComponent extends ControllerRenderProps<
    TFieldValues,
    TName
  > = ControllerRenderProps<TFieldValues, TName>
> extends Omit<ControllerProps<TFieldValues, TName>, "render"> {
  label?: string;
  description?: string;
  className?: string;
  component: React.ComponentType<TComponent>;
  /**
   * @description makes label go after component
   **/
  inline?: boolean;
  /**
   * @description a hint inside tooltip after label
   */
  hint?: string;
  /**
   * @description removes FormControl wrapper, needed for custom components
   */
  raw?: boolean;

  fieldDeps?: any[];
}

const Field = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  label,
  description,
  component: Component,
  inline,
  raw,
  className,
  hint,
  fieldDeps,
  ...controllerProps
}: FieldProps<TFieldValues, TName>) => {
  const Wrapper = raw ? React.Fragment : FormControl;

  return (
    <FormField
      {...controllerProps}
      render={({ field }: { field: ControllerRenderProps<TFieldValues, TName> }) => (
        <FormItem
          className={`${inline ? styles.itemInline : ""}${className ? ` ${className}` : ""}`.trim()}
        >
          {label && <FormLabel>{label}</FormLabel>}
          {description && <FormDescription>{description}</FormDescription>}
          <Wrapper>
            <Component {...field} />
          </Wrapper>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
  Field
};
